import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, MenuItem, Switch, TextField, Typography,
} from "@mui/material";
import {
  CalculateOutlined, CalendarTodayOutlined, Close, ConfirmationNumberOutlined,
  ContentCopyOutlined, GroupAddOutlined, InfoOutlined, MonetizationOnOutlined,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import CategoriaVoucherSelectorDialog from "./CategoriaVoucherSelectorDialog";
import ProductoVoucherSelectorDialog from "./ProductoVoucherSelectorDialog";
import { calculatePhysicalPaperForm, getPhysicalPaperForm } from "./voucherFisicoForm";

const nuevoForm = () => ({
  desde: dayjs().format("YYYY-MM-DD"), hasta: dayjs().add(1, "month").format("YYYY-MM-DD"),
  tipo: "MONTO", cantidadPapeles: "", valorPapel: "", limitarPorDia: false, papelesPorDia: "",
  idProducto: null, producto: null, idCategoria: null, categoria: null,
});
const inputSx = {
  "& .MuiInputBase-root": {
    width: "100% !important", display: "flex !important", minHeight: 40,
    borderRadius: "8px", bgcolor: "#fff", color: "#22365C", fontSize: 13,
    boxShadow: "0 1px 3px rgba(25, 46, 88, .04)",
    "& fieldset": { borderColor: "#D9E2F0" },
    "&:hover fieldset": { borderColor: "#A9BEE4" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(37, 99, 235, .09)" },
    "&.Mui-focused fieldset": { borderColor: "#3B74F2", borderWidth: 1 },
  },
  "& .MuiInputBase-input": {
    width: "100% !important", py: 1.1,
    "&::placeholder": { color: "#9AAECB", opacity: 1 },
  },
  "& .MuiSelect-icon": { color: "#526B96", fontSize: 20 },
  "& .MuiInputAdornment-root": { color: "#7187AE" },
  "&& .MuiInputAdornment-positionStart > .MuiSvgIcon-root": { fontSize: "19px !important" },
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": { WebkitAppearance: "none", m: 0 },
  "& input[type=date]::-webkit-calendar-picker-indicator": { display: "none" },
};
const formato = (value, monto) => monto
  ? new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value)
  : `${new Intl.NumberFormat("es-AR").format(value)} unidades`;

export default function VoucherFisicoBuilder({ open, onClose, onSubmit, categorias, editingConfiguration }) {
  const [form, setForm] = useState(nuevoForm);
  const [error, setError] = useState(null);
  const [selector, setSelector] = useState(null);
  const [baseline, setBaseline] = useState("");
  const [descartar, setDescartar] = useState(false);
  const fieldRefs = useRef({});
  const initial = editingConfiguration?.vouchers?.[0];
  useEffect(() => {
    if (!open) return;
    const next = initial ? { ...nuevoForm(), ...initial, ...getPhysicalPaperForm(initial) } : nuevoForm();
    setForm(next); setBaseline(JSON.stringify(next)); setError(null); setSelector(null); setDescartar(false);
  }, [open, initial]);
  const setField = (key, value) => { setForm((prev) => ({ ...prev, [key]: value })); setError(null); };
  const cerrar = () => {
    if (JSON.stringify(form) !== baseline) setDescartar(true);
    else onClose();
  };
  const calculated = calculatePhysicalPaperForm(form);
  const esMonto = form.tipo === "MONTO";
  const legado = Boolean(initial && (getPhysicalPaperForm(initial).cantidadPapeles === ""
    || (initial.limiteDiario != null && getPhysicalPaperForm(initial).papelesPorDia === "")));
  const submit = () => {
    const conservaDesde = initial && form.desde === initial.desde;
    let invalid = calculated.message ? calculated : null;
    if (!invalid && (!dayjs(form.desde).isValid() || !form.desde || !form.hasta || !dayjs(form.hasta).isValid()
      || (!conservaDesde && dayjs(form.desde).isBefore(dayjs().startOf("day")))
      || dayjs(form.hasta).isBefore(dayjs(form.desde)))) {
      invalid = { field: "desde", message: "Indicá una vigencia válida, desde hoy o una fecha futura." };
    }
    if (!invalid && form.tipo === "PRODUCTO" && !form.idProducto) invalid = { message: "Seleccioná el producto que cubre cada papel." };
    if (!invalid && form.tipo === "CATEGORIA" && !form.idCategoria) invalid = { message: "Seleccioná la categoría que cubre cada papel." };
    if (invalid) {
      setError(invalid); fieldRefs.current[invalid.field]?.focus(); return;
    }
    const fields = {
      desde: form.desde, hasta: form.hasta, tipo: form.tipo,
      cantidad: calculated.cantidad, limitePedido: calculated.limitePedido, limiteDiario: calculated.limiteDiario,
      idProducto: form.tipo === "PRODUCTO" ? Number(form.idProducto) : null,
      producto: form.tipo === "PRODUCTO" ? form.producto : null,
      idCategoria: form.tipo === "CATEGORIA" ? Number(form.idCategoria) : null,
      categoria: form.tipo === "CATEGORIA" ? form.categoria : null,
      idCliente: null, cliente: null,
    };
    const sources = editingConfiguration?.vouchers || [{ tempId: `fisico-${Date.now()}`, habilitado: true }];
    onSubmit(sources.map((voucher) => ({
      replaceTempId: initial ? voucher.tempId : null,
      voucher: { ...voucher, ...fields, estadoLocal: voucher.id ? "Editado" : "Pendiente" },
    })));
  };
  const field = (key, label, extra = {}) => {
    const { icon, required, ...fieldOptions } = extra;
    const isDate = fieldOptions.type === "date";
    return <Box sx={{ minWidth: 0 }}>
      <Typography component="label" htmlFor={`fisico-${key}`} display="block"
        sx={{ mb: 1, color: "#23365D", fontSize: 12.5, fontWeight: 600 }}>
        {label}{required && <Box component="span" aria-hidden="true" sx={{ ml: 0.5, color: "#E04C5A" }}>*</Box>}
      </Typography>
      <TextField fullWidth size="small" id={`fisico-${key}`} value={form[key]} required={required}
        inputRef={(element) => { fieldRefs.current[key] = element; }}
        onChange={(e) => setField(key, e.target.value)} sx={inputSx} {...fieldOptions}
        error={error?.field === key} helperText={error?.field === key ? error.message : fieldOptions.helperText}
        InputProps={{
          startAdornment: isDate ? <InputAdornment position="start">
            <IconButton size="small" aria-label={`Elegir fecha: ${label}`} sx={{ p: 0, color: "#7187AE" }}
              onClick={() => {
                const input = fieldRefs.current[key];
                if (input?.showPicker) input.showPicker();
                else input?.focus();
              }}><CalendarTodayOutlined sx={{ fontSize: 17 }} /></IconButton>
          </InputAdornment> : icon ? <InputAdornment position="start">{icon}</InputAdornment> : undefined,
          endAdornment: isDate ? <InputAdornment position="end">
            <IconButton size="small" aria-label={`Limpiar fecha: ${label}`} disabled={!form[key]}
              onClick={() => setField(key, "")} sx={{ p: 0.25, color: "#7D91B5" }}>
              <Close sx={{ fontSize: 15 }} />
            </IconButton>
          </InputAdornment> : undefined,
        }}
        inputProps={{ "aria-label": label, ...fieldOptions.inputProps }} />
    </Box>;
  };
  return <>
    <Dialog open={open} onClose={cerrar} fullWidth maxWidth="sm" aria-labelledby="voucher-fisico-title"
      PaperProps={{ sx: {
        m: { xs: 2, sm: 3 }, width: { xs: "calc(100% - 32px)", sm: "100%" },
        maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
        borderRadius: "12px", border: "1px solid #E0E6F0",
        background: "linear-gradient(135deg, #FFFFFF 0%, #FAFCFF 100%)",
        boxShadow: "0 22px 70px rgba(15, 31, 62, .24)",
      } }}>
      <DialogTitle component="div" sx={{ px: { xs: 2.25, sm: 2.75 }, pt: 2.25, pb: 3, display: "flex", gap: 2, alignItems: "center", pr: 5.5 }}>
        <Box sx={{ flexShrink: 0, width: 52, height: 52, borderRadius: "50%", bgcolor: "#E7EFFF", display: "grid", placeItems: "center", color: "#1E5CFF", "& .MuiSvgIcon-root": { fontSize: "27px !important" } }}>
          <ConfirmationNumberOutlined sx={{ fontSize: 27, transform: "rotate(-20deg)" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography id="voucher-fisico-title" component="h2" sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 750, lineHeight: 1.3, color: "#14284F" }}>
            {initial ? "Editar tanda de vouchers físicos" : "Agregar tanda de vouchers físicos"}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12.5, lineHeight: 1.5, color: "#7184A7" }}>Definí cuántos papeles vas a entregar y qué cubre cada uno.</Typography>
        </Box>
        <IconButton aria-label="Cerrar tanda" onClick={cerrar} sx={{ position: "absolute", top: 12, right: 10, p: 0.75, color: "#6C7F9E" }}><Close sx={{ fontSize: 20 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2.75, minWidth: 0, px: { xs: 2.25, sm: 2.75 }, pt: "0 !important", pb: 2.75 }}>
        {legado && <Alert severity="warning">Esta tanda anterior no representa papeles enteros. Para modificarla, indicá una cantidad entera de papeles y revisá el presupuesto calculado. Los consumos anteriores no se modifican.</Alert>}
        {error && <Alert severity="error">{error.message}</Alert>}
        {field("tipo", "¿Qué cubre cada papel?", { select: true, required: true, onChange: (e) => {
          setForm((prev) => ({ ...prev, tipo: e.target.value, valorPapel: "", idProducto: null, producto: null, idCategoria: null, categoria: null })); setError(null);
        }, children: [<MenuItem key="MONTO" value="MONTO">Un importe en pesos</MenuItem>, <MenuItem key="PRODUCTO" value="PRODUCTO">Un producto</MenuItem>, <MenuItem key="CATEGORIA" value="CATEGORIA">Productos de una categoría</MenuItem>] })}
        {!esMonto && <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="body2">{form.tipo === "PRODUCTO" ? form.producto?.nombre || "Elegí el producto incluido" : form.categoria?.categoria || "Elegí la categoría incluida"}</Typography>
          <Button variant="outlined" onClick={() => setSelector(form.tipo)} sx={{
            color: "#2458C7", borderColor: "#CBD9F3", borderRadius: "8px", textTransform: "none", fontSize: 12.5,
            "&:hover": { color: "#1743A5", borderColor: "#2458C7", bgcolor: "#F0F5FF" },
            "&.Mui-focusVisible": { outline: "3px solid #8FA7E8", outlineOffset: 2 },
          }}>Seleccionar {form.tipo === "PRODUCTO" ? "producto" : "categoría"}</Button>
        </Box>}
        <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          {field("cantidadPapeles", "Cantidad de papeles", { type: "number", required: true, icon: <ContentCopyOutlined sx={{ fontSize: 19 }} />, inputProps: { min: 1, step: 1 }, placeholder: "Ej. 100" })}
          {field("valorPapel", esMonto ? "Valor de cada papel ($)" : "Unidades por papel", { type: "number", required: true, icon: <MonetizationOnOutlined sx={{ fontSize: 19 }} />, inputProps: { min: esMonto ? 0.01 : 1, step: esMonto ? 0.01 : 1 }, placeholder: esMonto ? "Ej. 2500" : "Ej. 2" })}
        </Box>
        <Box aria-live="polite" sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", background: "linear-gradient(110deg, #EAF3FF 0%, #F1F5FF 100%)", border: "1px solid #D5E4FF", borderRadius: "9px" }}>
          <Box sx={{ pr: 2, borderRight: "1px solid #B8D1FF", display: "flex", alignItems: "center", alignSelf: "stretch" }}>
            <Box sx={{ width: 37, height: 37, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: "#2463FF", color: "#fff" }}><CalculateOutlined sx={{ fontSize: 23 }} /></Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, color: "#7189B1", lineHeight: 1.5 }}>Monto total</Typography>
            <Typography data-testid="presupuesto-tanda" sx={{ mt: 0.25, fontSize: 17, fontWeight: 750, lineHeight: 1.4, color: "#1455E5" }}>
              {calculated.cantidad != null ? formato(calculated.cantidad, esMonto) : "Completá los papeles y su valor"}
            </Typography>
            {calculated.cantidad != null && <Typography sx={{ mt: 0.25, fontSize: 11, color: "#647FA8" }}>{form.cantidadPapeles} papeles × {formato(calculated.limitePedido, esMonto)} por papel</Typography>}
          </Box>
        </Box>
        <Box>
          <FormControlLabel sx={{ m: 0, gap: 1.5, "& .MuiFormControlLabel-label": { color: "#23365D", fontSize: 12.5, fontWeight: 600 } }} control={<Switch checked={form.limitarPorDia} onChange={(e) => setField("limitarPorDia", e.target.checked)} sx={{
            width: 42, height: 24, p: 0,
            "& .MuiSwitch-switchBase": { p: "3px", top: "0 !important", left: 0, color: "#fff", transform: "translateX(0)" },
            "& .MuiSwitch-thumb": { width: 18, height: 18, boxShadow: "0 1px 3px rgba(15, 31, 62, .2)" },
            "& .MuiSwitch-track": { opacity: 1, background: "#CAD2DF !important", border: "none !important", borderRadius: "12px" },
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff", transform: "translateX(18px)" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: "#2463FF !important", opacity: 1 },
          }} />} label="Limitar papeles por día" />
          <Typography sx={{ mt: 0.75, ml: "54px", fontSize: 10.5, lineHeight: 1.6, color: "#8293B2" }}>{form.limitarPorDia ? "El límite se comparte entre todas las personas y cajas de esta tanda." : "Sin límite diario: se pueden usar hasta agotar la tanda."}</Typography>
          {form.limitarPorDia && <Box mt={1.5}>
            {field("papelesPorDia", "Máximo de papeles por día", { type: "number", required: true, icon: <ContentCopyOutlined sx={{ fontSize: 19 }} />, inputProps: { min: 1, step: 1 },
              helperText: calculated.limiteDiario != null ? `Equivale a ${formato(calculated.limiteDiario, esMonto)} por día.` : "Ingresá papeles completos, no unidades ni pesos." })}
          </Box>}
        </Box>
        <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          {field("desde", "Válidos desde", { type: "date", inputProps: { min: initial?.desde || dayjs().format("YYYY-MM-DD") } })}
          {field("hasta", "Válidos hasta", { type: "date", inputProps: { min: form.desde } })}
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, color: "#7E91B3" }}>
          <InfoOutlined sx={{ fontSize: 16, mt: "1px", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 10.5, lineHeight: 1.65 }}>Los papeles no tienen un beneficiario asignado. Al agregar la tanda, todavía falta guardar el grupo.</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.25, sm: 2.75 }, py: 2, gap: 1.25, borderTop: "1px solid #E9EDF5" }}>
        <Button onClick={cerrar} sx={{ color: "#7187AE", textTransform: "none", fontSize: 12, fontWeight: 600, px: 2 }}>Cancelar</Button>
        <Button variant="contained" onClick={submit} startIcon={<GroupAddOutlined sx={{ fontSize: "18px !important" }} />} sx={{
          minHeight: 40, px: 2, borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 600, color: "#fff",
          background: "linear-gradient(110deg, #1555FF 0%, #245BFF 100%)", boxShadow: "0 3px 7px rgba(25, 80, 240, .22)",
          "&:hover": { background: "#1749E0", boxShadow: "0 4px 10px rgba(25, 80, 240, .28)" },
        }}>{initial ? "Guardar tanda" : "Agregar al grupo"}</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={descartar} onClose={() => setDescartar(false)}><DialogTitle>¿Descartar los cambios de la tanda?</DialogTitle><DialogActions><Button onClick={() => setDescartar(false)}>Seguir editando</Button><Button color="error" onClick={() => { setDescartar(false); onClose(); }}>Descartar</Button></DialogActions></Dialog>
    <CategoriaVoucherSelectorDialog open={selector === "CATEGORIA"} categorias={categorias} selectedId={form.idCategoria} onCancel={() => setSelector(null)} onConfirm={(categoria) => { setForm((prev) => ({ ...prev, idCategoria: categoria.id, categoria })); setSelector(null); setError(null); }} />
    <ProductoVoucherSelectorDialog open={selector === "PRODUCTO"} categorias={categorias} selectedProducto={form.producto} onCancel={() => setSelector(null)} onConfirm={(producto) => { setForm((prev) => ({ ...prev, idProducto: producto.id, producto })); setSelector(null); setError(null); }} />
  </>;
}

VoucherFisicoBuilder.propTypes = {
  open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onSubmit: PropTypes.func.isRequired,
  categorias: PropTypes.array.isRequired, editingConfiguration: PropTypes.object,
};
